export interface LineImportDetailImport {
  id: string
  liver_id: string
  liver?: {
    id: string
    display_name: string
    status: string
  } | null
  file_name: string
  message_count: number
  active_days: number
  date_start: string | null
  date_end: string | null
  primary_manager_name: string | null
  sender_aliases?: LineSenderAlias[]
  analysis?: unknown
  handoff_note: string | null
  created_at: string
}

export interface LineSenderAlias {
  id: string
  entity_type: 'manager' | 'liver'
  line_name: string
  normalized_line_name: string
  manager_id: string | null
  liver_id: string | null
  status: string
}

export interface LineSenderAliasCandidate {
  id: string
  line_import_id: string
  liver_id: string
  observed_name: string
  normalized_observed_name: string
  suggested_entity_type: 'manager' | 'liver' | null
  suggested_manager_id: string | null
  suggested_liver_id: string | null
  reason: string | null
  confidence: number
  status: 'pending' | 'accepted' | 'rejected' | 'ignored'
  decided_by: string | null
  decided_at: string | null
  created_at: string
  updated_at: string
}

export interface LineAliasCandidateManager {
  id: string
  name?: string | null
  email?: string | null
}

export interface LineAliasCandidateLiver {
  id: string
  display_name: string
  status?: string | null
}

export interface LineAliasCandidateRow {
  id: string
  observedName: string
  previousLineName: string | null
  targetLabel: string
  entityTypeLabel: string
  reason: string
  confidenceLabel: string
  confidence: number
  candidate: LineSenderAliasCandidate
}

export interface LineImportDetailMessage {
  id: string
  message_index: number | null
  sent_date: string | null
  sent_time: string | null
  sender: string
  body: string
  message_type: string
  sender_type: string
  sender_manager_id?: string | null
  sender_liver_id?: string | null
}

export type LineMessageSpeakerRole = 'manager' | 'liver' | 'other' | 'system'
export type LineMessageBubbleSide = 'left' | 'right' | 'center'

export interface LineImportDetailMessageRow extends LineImportDetailMessage {
  speakerRole: LineMessageSpeakerRole
  bubbleSide: LineMessageBubbleSide
  messageTypeLabel: string
}

export interface LineImportDetailSenderRow {
  sender: string
  messageCount: number
  speakerRole: LineMessageSpeakerRole
}

export interface LineImportDetailDayGroup {
  date: string
  messages: LineImportDetailMessageRow[]
}

export interface LineImportDetailMessageGroup {
  id: string
  date: string
  sender: string
  speakerRole: LineMessageSpeakerRole
  bubbleSide: LineMessageBubbleSide
  messageTypeLabel: string
  firstTime: string | null
  lastTime: string | null
  messages: LineImportDetailMessageRow[]
}

export interface LineImportDetailMessageGroupDay {
  date: string
  messageGroups: LineImportDetailMessageGroup[]
}

export interface LineImportDetailModel {
  lineImport: LineImportDetailImport
  messages: LineImportDetailMessageRow[]
  liverName: string
  periodLabel: string
  primaryManagerName: string | null
  riskCount: number
  highRiskCount: number
  handoffSignalCount: number
  topicLabels: string[]
  senderRows: LineImportDetailSenderRow[]
  dayGroups: LineImportDetailDayGroup[]
}

export type LineImportDetailRoleFilter = LineMessageSpeakerRole | 'all'

export interface LineImportDetailMessageFilters {
  query?: string
  sender?: string
  role?: LineImportDetailRoleFilter
  hideEvents?: boolean
}

export interface LineImportDetailMessageView {
  messages: LineImportDetailMessageRow[]
  dayGroups: LineImportDetailMessageGroupDay[]
}

type LineAnalysis = {
  risks?: Array<{ severity?: string | null } | null> | null
  handoffSignals?: unknown[] | null
  topics?: Array<{ topic?: string | null } | null> | null
}

const TOPIC_LABELS: Record<string, string> = {
  onboarding: 'オンボーディング',
  schedule_call: '日程・通話',
  payment_tax: '支払・税務',
  event: 'イベント',
  rights_rules: '権利・ルール',
  motivation: 'モチベーション'
}

const MESSAGE_TYPE_LABELS: Record<string, string> = {
  text: 'テキスト',
  photo: '写真',
  sticker: 'スタンプ',
  video: '動画',
  file: 'ファイル',
  deleted: '削除済み',
  system: 'システム',
  call_start: '通話開始',
  call_end: '通話終了'
}

export function buildLineImportDetail(
  lineImport: LineImportDetailImport,
  messages: LineImportDetailMessage[]
): LineImportDetailModel {
  const sortedMessages = [...messages]
    .sort(compareMessageIndex)
    .map((message) => buildMessageRow(message, lineImport))
  const analysis = readAnalysis(lineImport.analysis)
  const senderRows = buildSenderRows(sortedMessages)

  return {
    lineImport,
    messages: sortedMessages,
    liverName: lineImport.liver?.display_name ?? '名称未設定',
    periodLabel: buildPeriodLabel(lineImport.date_start, lineImport.date_end),
    primaryManagerName: lineImport.primary_manager_name,
    riskCount: analysis.risks?.length ?? 0,
    highRiskCount: (analysis.risks ?? []).filter((risk) => risk?.severity === 'high').length,
    handoffSignalCount: analysis.handoffSignals?.length ?? 0,
    topicLabels: [...new Set((analysis.topics ?? []).flatMap((topic) => topicLabel(topic?.topic)))],
    senderRows,
    dayGroups: groupMessagesByDate(sortedMessages)
  }
}

export function buildLineImportMessageView(
  model: LineImportDetailModel,
  filters: LineImportDetailMessageFilters
): LineImportDetailMessageView {
  const messages = model.messages.filter((message) => matchesMessageFilter(message, filters))
  return {
    messages,
    dayGroups: groupMessageRowsByDate(messages).map((group) => ({
      date: group.date,
      messageGroups: groupConsecutiveMessages(group.messages, group.date)
    }))
  }
}

export function buildAliasCandidateRows(
  candidates: LineSenderAliasCandidate[],
  managers: LineAliasCandidateManager[],
  liver: LineAliasCandidateLiver | null | undefined
): LineAliasCandidateRow[] {
  return candidates
    .filter((candidate) => candidate.status === 'pending' && candidate.suggested_entity_type)
    .map((candidate) => ({
      id: candidate.id,
      observedName: candidate.observed_name,
      previousLineName: extractPreviousLineName(candidate.reason),
      targetLabel: aliasCandidateTargetLabel(candidate, managers, liver),
      entityTypeLabel: candidate.suggested_entity_type === 'manager' ? 'マネージャー' : 'ライバー',
      reason: candidate.reason ?? 'LINE名が変わった可能性があります。',
      confidenceLabel: `${Math.round(clampNumber(candidate.confidence, 0, 1) * 100)}%`,
      confidence: clampNumber(candidate.confidence, 0, 1),
      candidate
    }))
    .sort((a, b) => b.confidence - a.confidence || a.observedName.localeCompare(b.observedName, 'ja'))
}

function buildMessageRow(
  message: LineImportDetailMessage,
  lineImport: LineImportDetailImport
): LineImportDetailMessageRow {
  const speakerRole = classifySpeakerRole(message, lineImport)
  return {
    ...message,
    speakerRole,
    bubbleSide: bubbleSideForRole(speakerRole),
    messageTypeLabel: MESSAGE_TYPE_LABELS[message.message_type] ?? message.message_type
  }
}

function classifySpeakerRole(
  message: LineImportDetailMessage,
  lineImport: LineImportDetailImport
): LineMessageSpeakerRole {
  if (message.sender_type === 'system' || message.message_type.startsWith('call_') || !message.sender.trim()) {
    return 'system'
  }

  if (message.sender_type === 'manager' || message.sender_manager_id) {
    return 'manager'
  }

  if (message.sender_type === 'liver' || message.sender_liver_id) {
    return 'liver'
  }

  const aliasRole = findAliasSpeakerRole(message.sender, lineImport.sender_aliases)
  if (aliasRole) return aliasRole

  if (sameLineName(message.sender, lineImport.primary_manager_name)) {
    return 'manager'
  }

  if (sameLineName(message.sender, lineImport.liver?.display_name ?? null)) {
    return 'liver'
  }

  return 'other'
}

function findAliasSpeakerRole(
  sender: string,
  aliases: LineSenderAlias[] | undefined
): Exclude<LineMessageSpeakerRole, 'other' | 'system'> | null {
  const normalizedSender = normalizeLineName(sender)
  if (!normalizedSender) return null
  const alias = (aliases ?? []).find((item) => {
    if (item.status !== 'active') return false
    return normalizeLineName(item.normalized_line_name || item.line_name) === normalizedSender
  })
  if (!alias) return null
  return alias.entity_type === 'manager' ? 'manager' : 'liver'
}

function bubbleSideForRole(role: LineMessageSpeakerRole): LineMessageBubbleSide {
  if (role === 'manager') return 'right'
  if (role === 'system') return 'center'
  return 'left'
}

function sameLineName(a: string | null | undefined, b: string | null | undefined): boolean {
  const normalizedA = normalizeLineName(a)
  const normalizedB = normalizeLineName(b)
  return normalizedA.length > 0 && normalizedA === normalizedB
}

function normalizeLineName(value: string | null | undefined): string {
  return (value ?? '')
    .normalize('NFKC')
    .trim()
    .replace(/\s+/g, ' ')
    .toLowerCase()
}

function normalizeSearchText(value: string | null | undefined): string {
  return (value ?? '')
    .normalize('NFKC')
    .trim()
    .toLowerCase()
}

function buildSenderRows(messages: LineImportDetailMessageRow[]): LineImportDetailSenderRow[] {
  const counts = new Map<string, { count: number; firstIndex: number; speakerRole: LineMessageSpeakerRole }>()
  messages.forEach((message, index) => {
    const sender = message.sender.trim() || 'システム'
    const current = counts.get(sender)
    counts.set(sender, {
      count: (current?.count ?? 0) + 1,
      firstIndex: current?.firstIndex ?? index,
      speakerRole: current?.speakerRole ?? message.speakerRole
    })
  })
  return [...counts.entries()]
    .map(([sender, value]) => ({
      sender,
      messageCount: value.count,
      firstIndex: value.firstIndex,
      speakerRole: value.speakerRole
    }))
    .sort((a, b) => b.messageCount - a.messageCount || a.firstIndex - b.firstIndex)
    .map(({ sender, messageCount, speakerRole }) => ({ sender, messageCount, speakerRole }))
}

function groupMessagesByDate(messages: LineImportDetailMessageRow[]): LineImportDetailDayGroup[] {
  return groupMessageRowsByDate(messages).map((group) => ({
    date: group.date,
    messages: group.messages
  }))
}

function groupMessageRowsByDate(messages: LineImportDetailMessageRow[]): LineImportDetailDayGroup[] {
  const groups = new Map<string, LineImportDetailMessageRow[]>()
  for (const message of messages) {
    const date = message.sent_date ?? '日付不明'
    const list = groups.get(date) ?? []
    list.push(message)
    groups.set(date, list)
  }
  return [...groups.entries()].map(([date, groupedMessages]) => ({ date, messages: groupedMessages }))
}

function groupConsecutiveMessages(
  messages: LineImportDetailMessageRow[],
  date: string
): LineImportDetailMessageGroup[] {
  const groups: LineImportDetailMessageGroup[] = []

  for (const message of messages) {
    const sender = message.sender.trim() || 'システム'
    const previous = groups.at(-1)
    if (previous && canAppendToGroup(previous, message, sender)) {
      previous.messages.push(message)
      previous.lastTime = message.sent_time
      continue
    }

    groups.push({
      id: message.id,
      date,
      sender,
      speakerRole: message.speakerRole,
      bubbleSide: message.bubbleSide,
      messageTypeLabel: message.messageTypeLabel,
      firstTime: message.sent_time,
      lastTime: message.sent_time,
      messages: [message]
    })
  }

  return groups
}

function canAppendToGroup(
  group: LineImportDetailMessageGroup,
  message: LineImportDetailMessageRow,
  sender: string
): boolean {
  return group.sender === sender &&
    group.speakerRole === message.speakerRole &&
    group.bubbleSide === message.bubbleSide &&
    group.bubbleSide !== 'center'
}

function matchesMessageFilter(
  message: LineImportDetailMessageRow,
  filters: LineImportDetailMessageFilters
): boolean {
  if (filters.hideEvents && message.message_type !== 'text') return false
  if (filters.sender && (message.sender.trim() || 'システム') !== filters.sender) return false
  if (filters.role && filters.role !== 'all' && message.speakerRole !== filters.role) return false

  const query = normalizeSearchText(filters.query)
  if (!query) return true

  return [
    message.sender,
    message.body,
    message.messageTypeLabel
  ].some((value) => normalizeSearchText(value).includes(query))
}

function readAnalysis(value: unknown): LineAnalysis {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {}
  const src = value as LineAnalysis
  return {
    risks: Array.isArray(src.risks) ? src.risks : [],
    handoffSignals: Array.isArray(src.handoffSignals) ? src.handoffSignals : [],
    topics: Array.isArray(src.topics) ? src.topics : []
  }
}

function aliasCandidateTargetLabel(
  candidate: LineSenderAliasCandidate,
  managers: LineAliasCandidateManager[],
  liver: LineAliasCandidateLiver | null | undefined
): string {
  if (candidate.suggested_entity_type === 'manager') {
    const manager = managers.find((item) => item.id === candidate.suggested_manager_id)
    return manager?.name || manager?.email || 'マネージャー'
  }
  if (candidate.suggested_entity_type === 'liver') {
    return liver?.display_name ?? 'ライバー'
  }
  return '未設定'
}

function extractPreviousLineName(reason: string | null): string | null {
  const match = reason?.match(/既存のLINE名「([^」]+)」/)
  return match?.[1] ?? null
}

function clampNumber(value: number, min: number, max: number): number {
  if (!Number.isFinite(value)) return min
  return Math.max(min, Math.min(max, value))
}

function topicLabel(topic: string | null | undefined): string[] {
  if (!topic) return []
  return [TOPIC_LABELS[topic] ?? topic]
}

function buildPeriodLabel(start: string | null, end: string | null): string {
  if (start && end) return `${start}〜${end}`
  return start ?? end ?? '-'
}

function compareMessageIndex(a: LineImportDetailMessage, b: LineImportDetailMessage): number {
  return safeIndex(a.message_index) - safeIndex(b.message_index)
}

function safeIndex(value: number | null): number {
  return Number.isFinite(value) ? value ?? 0 : 0
}
